import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastService } from './toast.service';
import { SpinnerService } from './spinner.service';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';
import { DashboardResponse, IDashboardResponse } from '../models/dashboard-response.model';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import {
  IDashboardDevicesBrandsResponse,
  IDashboardDevicesFoldablesResponse,
  IDashboardDevicesModelsResponse,
  IDashboardDevicesOsResponse
} from '../models/dashboard-devices-response.model';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  loading: boolean = false;
  public dashboard?: DashboardResponse;

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService,
    private authService: AuthService
  ) {}


  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR getAll                               */
  /* -------------------------------------------------------------------------- */
  // region getAll
  async getAll(
    startDate?: string,
  ): Promise<any> {
    this.loading = true;
    let urlApi = `${environment.urlApi}/dashboard`;

    if (startDate) {
      // If a start date is provided, append it to the URL
      urlApi += `?start_date=${startDate}`;
    }

    let data: any = null;

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.get<IServerResponse>(
            urlApi,
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response) => resolve(new ServerResponse(response)),
            error: (error) => reject(error)
          });
        }
      );

      console.log('Dashboard response:', response);
      

      if (response.statusCode === 200) {
        data = response.data;
      }

    } catch (error) {

      console.error('Error fetching dashboard data:', error);

      this.toastService.show({
        header: 'Error de servidor',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.loading = false;

    return data;
  }
  

  /* -------------------------------------------------------------------------- */
  /*                       ANCHOR getAllAboutNeighborhood                       */
  /* -------------------------------------------------------------------------- */
  // region getAllAboutNeighborhood
  async getAllAboutNeighborhood(
    neighborhoodId: string,
    startDate?: string,
  ) {
    this.loading = true;
    let urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/dashboard`;

    if (startDate) {
      // If a start date is provided, append it to the URL
      urlApi += `?start_date=${startDate}`;
    }

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<IDashboardResponse>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<IDashboardResponse>>(
            urlApi,
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response) => resolve(new ServerResponse<IDashboardResponse>(response)),
            error: (error) => reject(error)
          });
        }
      );

      console.log('Dashboard response:', response);
      

      if (response.statusCode === 200) {
        this.dashboard = new DashboardResponse(response.data!);
      }

    } catch (error) {

      console.error('Error fetching dashboard data:', error);

      this.toastService.show({
        header: 'Error de servidor',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.loading = false;
  }
  

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR getDevicesByOs                          */
  /* -------------------------------------------------------------------------- */
  // region getDevicesByOs
  /**
   * Celulares agrupados por sistema operativo.
   *
   * A diferencia de getAll(), los métodos de celulares NO muestran toast ni
   * tocan `this.loading`: cada tarjeta de la sección pinta su propio estado de
   * error con "Reintentar", así que el error se propaga al componente.
   */
  async getDevicesByOs(
    includeInactive: boolean
  ): Promise<IDashboardDevicesOsResponse> {
    return this.getDevicesStats<IDashboardDevicesOsResponse>('os', includeInactive);
  }


  /* -------------------------------------------------------------------------- */
  /*                           ANCHOR getDevicesByBrand                         */
  /* -------------------------------------------------------------------------- */
  // region getDevicesByBrand
  /** Celulares agrupados por marca. Mismo manejo de errores que getDevicesByOs(). */
  async getDevicesByBrand(
    includeInactive: boolean
  ): Promise<IDashboardDevicesBrandsResponse> {
    return this.getDevicesStats<IDashboardDevicesBrandsResponse>('brands', includeInactive);
  }


  /* -------------------------------------------------------------------------- */
  /*                           ANCHOR getDevicesByModel                         */
  /* -------------------------------------------------------------------------- */
  // region getDevicesByModel
  /** Celulares agrupados por modelo. Mismo manejo de errores que getDevicesByOs(). */
  async getDevicesByModel(
    includeInactive: boolean
  ): Promise<IDashboardDevicesModelsResponse> {
    return this.getDevicesStats<IDashboardDevicesModelsResponse>('models', includeInactive);
  }


  /* -------------------------------------------------------------------------- */
  /*                          ANCHOR getDevicesFoldables                        */
  /* -------------------------------------------------------------------------- */
  // region getDevicesFoldables
  /** Celulares plegables (solo Android). Mismo manejo de errores que getDevicesByOs(). */
  async getDevicesFoldables(
    includeInactive: boolean
  ): Promise<IDashboardDevicesFoldablesResponse> {
    return this.getDevicesStats<IDashboardDevicesFoldablesResponse>('foldables', includeInactive);
  }


  /* -------------------------------------------------------------------------- */
  /*                           ANCHOR getDevicesStats                           */
  /* -------------------------------------------------------------------------- */
  /**
   * GET /dashboard/devices/{group}?includeInactive=...
   * Lanza el error (o uno propio si el statusCode no es 200) para que la
   * tarjeta que lo pidió decida cómo mostrarlo.
   */
  private async getDevicesStats<T>(
    group: 'os' | 'brands' | 'models' | 'foldables',
    includeInactive: boolean
  ): Promise<T> {
    const urlApi = `${environment.urlApi}/dashboard/devices/${group}?includeInactive=${includeInactive}`;

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<T>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<T>>(
            urlApi,
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response) => resolve(new ServerResponse<T>(response)),
            error: (error) => reject(error)
          });
        }
      );

      if (response.statusCode !== 200 || !response.data) {
        throw new Error(response.message || `Error ${response.statusCode}`);
      }

      return response.data;

    } catch (error) {

      console.error(`Error fetching dashboard devices (${group}):`, error);

      throw error;
    }
  }

}
